/**
 * Context pour la gestion du profil prestataire libre
 * Centralise l'état et les actions du profil
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { isPrestataireRole } from '../utils/roleUtils';
import * as profileService from '../services/profileService';
import * as documentService from '../services/documentService';
import type {
  ProfileData,
  ProfileCompletion,
  PresentationData,
  CoordonneesData,
  BancaireData,
  EntrepriseFormData,  // FRONT-24
  Document,
  DocumentStatus,
} from '../services/profileService';
import type { DocumentType } from '../services/documentService';

// ============================================
// Types
// ============================================

interface ProfileContextState {
  // Données
  profile: ProfileData | null;
  completion: ProfileCompletion | null;
  documents: Document[];
  documentsStatus: DocumentStatus[];

  // États
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  successMessage: string | null;

  // Actions
  loadProfile: () => Promise<void>;
  refreshCompletion: () => Promise<void>;
  updatePresentation: (data: PresentationData) => Promise<boolean>;
  updateCoordonnees: (data: CoordonneesData) => Promise<boolean>;
  updateBancaire: (data: BancaireData) => Promise<boolean>;
  updateEntreprise: (data: EntrepriseFormData) => Promise<boolean>;  // FRONT-24
  updateSiret: (siret: string) => Promise<boolean>;
  updateLots: (data: { lots_techniques: number[]; departements: number[]; regions: number[] }) => Promise<boolean>;
  updateLotsForLotTechnique: (lotTechniqueId: number, departements: number[]) => Promise<void>;
  uploadDocument: (file: File, type: DocumentType, commentaire?: string) => Promise<boolean>;
  deleteDocument: (documentId: number) => Promise<boolean>;
  downloadDocument: (documentKey: string, filename: string) => Promise<void>;
  clearMessages: () => void;
  // Protection navigation
  hasPendingChanges: boolean;
  reportAutoSaveStatus: (tabId: string, isPending: boolean) => void;
}

const ProfileContext = createContext<ProfileContextState | null>(null);

// ============================================
// Hook
// ============================================

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile doit être utilisé dans un ProfileProvider');
  }
  return context;
};

// ============================================
// Provider
// ============================================

interface ProfileProviderProps {
  children: React.ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // États
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [completion, setCompletion] = useState<ProfileCompletion | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentsStatus, setDocumentsStatus] = useState<DocumentStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Suivi des onglets avec sauvegardes en attente
  const pendingTabsRef = useRef(new Set<string>());
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const reportAutoSaveStatus = useCallback((tabId: string, isPending: boolean) => {
    if (isPending) {
      pendingTabsRef.current.add(tabId);
    } else {
      pendingTabsRef.current.delete(tabId);
    }
    setHasPendingChanges(pendingTabsRef.current.size > 0);
  }, []);

  // Chargement automatique du profil dès que l'utilisateur est authentifié
  useEffect(() => {
    if (isAuthenticated && user?.role && isPrestataireRole(user.role)) {
      loadProfile();
    } else {
      setProfile(null);
      setCompletion(null);
      setDocuments([]);
      setDocumentsStatus([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ============================================
  // Actions
  // ============================================

  /**
   * Charge les données complètes du profil
   */
  const loadProfile = useCallback(async () => {
    if (!isAuthenticated) return;
    if (!user?.role || !isPrestataireRole(user.role)) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await profileService.getProfile();
      console.log('Profile data loaded:', data);
      console.log('Completion:', data.completion);
      setProfile(data);
      setCompletion(data.completion);
      setDocuments(data.documents || []);
      setDocumentsStatus(data.documents_status || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement du profil';
      setError(message);
      console.error('Erreur loadProfile:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Rafraîchit uniquement les données de complétion
   */
  const refreshCompletion = useCallback(async () => {
    try {
      const data = await profileService.getProfileCompletion();
      setCompletion(data);

      // Mettre à jour aussi dans profile si présent
      if (profile) {
        setProfile(prev => prev ? { ...prev, completion: data } : null);
      }
    } catch (err) {
      console.error('Erreur refreshCompletion:', err);
    }
  }, [profile]);

  /**
   * Met à jour la section présentation
   */
  const updatePresentation = useCallback(async (data: PresentationData): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updatePresentation(data);
      // Merger la présentation dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        presentation: result.presentation ?? prev.presentation,
        completion: result.completion ?? prev.completion,
      } : null);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      setSuccessMessage('Présentation mise à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Met à jour la section coordonnées
   * BUGFIX: Merger les données partielles au lieu de remplacer tout le profil
   */
  const updateCoordonnees = useCallback(async (data: CoordonneesData): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateCoordonnees(data);
      // Merger les coordonnées dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        coordonnees: result.coordonnees ?? prev.coordonnees,
        completion: result.completion ?? prev.completion,
      } : null);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      setSuccessMessage('Coordonnées mises à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Met à jour la section bancaire
   * BUGFIX: Merger les données partielles au lieu de remplacer tout le profil
   */
  const updateBancaire = useCallback(async (data: BancaireData): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateBancaire(data);
      // Merger les données bancaires dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        bancaire: result.bancaire ?? prev.bancaire,
        completion: result.completion ?? prev.completion,
      } : null);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      setSuccessMessage('Coordonnées bancaires mises à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * FRONT-24: Met à jour les informations entreprise (sauf nom et siret)
   */
  const updateEntreprise = useCallback(async (data: EntrepriseFormData): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateEntreprise(data);
      // Merger les données entreprise dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        entreprise: result.entreprise ?? prev.entreprise,
        completion: result.completion ?? prev.completion,
      } : null);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      setSuccessMessage('Informations entreprise mises à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Met à jour le SIRET (uniquement si le SIRET actuel est vide)
   */
  const updateSiret = useCallback(async (siret: string): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateSiret(siret);
      // Merger le SIRET dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        entite: { ...prev.entite, siret: result.siret },
        entreprise: prev.entreprise ? { ...prev.entreprise, siret: result.siret } : null,
      } : null);
      setSuccessMessage('SIRET mis à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour du SIRET';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Met à jour les lots techniques et géographiques (prestataire libre)
   * Ancien format - gardé pour rétrocompatibilité
   */
  const updateLots = useCallback(async (data: {
    lots_techniques: number[];
    departements: number[];
    regions: number[]
  }): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateLots(data);
      // Merger les données dans le profil existant
      setProfile(prev => prev ? {
        ...prev,
        lots: result.lots ?? prev.lots,
        prestataire_libre: result.prestataire_libre ?? prev.prestataire_libre,
        completion: result.completion ?? prev.completion,
      } : result);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      setSuccessMessage('Zones d\'intervention mises à jour avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * FRONT-04: Met à jour les zones d'intervention pour un lot technique donné
   */
  const updateLotsForLotTechnique = useCallback(async (
    lotTechniqueId: number,
    departements: number[]
  ): Promise<void> => {
    setIsSaving(true);
    setError(null);

    try {
      const result = await profileService.updateLotsForLotTechnique(lotTechniqueId, departements);
      // Merger les données dans le profil existant pour ne pas perdre les autres champs
      setProfile(prev => prev ? {
        ...prev,
        lots: result.lots ?? prev.lots,
        prestataire_libre: result.prestataire_libre ?? prev.prestataire_libre,
        completion: result.completion ?? prev.completion,
      } : result);
      if (result.completion) {
        setCompletion(result.completion);
        window.dispatchEvent(new Event('profile-updated'));
      }
      // Pas de message de succès pour éviter le spam lors des clics rapides
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      setError(message);
      throw err; // Relancer pour que le composant puisse gérer
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Upload un document
   */
  const uploadDocument = useCallback(async (
    file: File,
    type: DocumentType,
    commentaire?: string
  ): Promise<boolean> => {
    // Validation côté client
    const validation = documentService.validateFile(file, type);
    if (!validation.valid) {
      setError(validation.error || 'Fichier invalide');
      return false;
    }

    setIsSaving(true);
    setError(null);

    try {
      const result = await documentService.uploadDocument(file, type, commentaire);

      // FRONT-06: Ajouter le nouveau document SANS retirer l'ancien
      // Historique conserve - tous les documents du meme type sont gardes
      setDocuments(prev => {
        // Ajouter le nouveau document a la liste existante
        return [...prev, result.document];
      });

      // Mettre a jour le statut des documents
      setDocumentsStatus(prev =>
        prev.map(status =>
          status.type === type ? { ...status, uploaded: true } : status
        )
      );

      // Rafraîchir la complétion complète (sections incluses) pour mettre à jour les pastilles
      await refreshCompletion();

      setSuccessMessage(`Document "${documentService.DOCUMENT_TYPE_LABELS[type]}" uploadé avec succès`);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de l\'upload';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [refreshCompletion]);
  const deleteDocument = useCallback(async (documentId: number): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      // Trouver le document pour récupérer son type
      const doc = documents.find(d => d.id === documentId);

      const result = await documentService.deleteDocument(documentId);

      // Retirer le document de la liste
      setDocuments(prev => prev.filter(d => d.id !== documentId));

      // Mettre à jour le statut si on a trouvé le document
      if (doc) {
        setDocumentsStatus(prev =>
          prev.map(status =>
            status.type === doc.type ? { ...status, uploaded: false } : status
          )
        );
      }

      // Rafraîchir la complétion complète (sections incluses) pour mettre à jour les pastilles
      await refreshCompletion();

      setSuccessMessage('Document supprimé avec succès');
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [documents, refreshCompletion]);

  /**
   * Télécharge un document
   */
  const downloadDocument = useCallback(async (documentKey: string, filename: string): Promise<void> => {
    try {
      const blob = await documentService.downloadDocument(documentKey);

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du téléchargement';
      setError(message);
    }
  }, []);

  /**
   * Efface les messages d'erreur/succès
   */
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccessMessage(null);
  }, []);

  // ============================================
  // Valeur du contexte
  // ============================================

  const value: ProfileContextState = {
    profile,
    completion,
    documents,
    documentsStatus,
    isLoading,
    isSaving,
    error,
    successMessage,
    loadProfile,
    refreshCompletion,
    updatePresentation,
    updateCoordonnees,
    updateBancaire,
    updateEntreprise,  // FRONT-24
    updateSiret,
    updateLots,
    updateLotsForLotTechnique,
    uploadDocument,
    deleteDocument,
    downloadDocument,
    clearMessages,
    hasPendingChanges,
    reportAutoSaveStatus,
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext;
